import { useEffect } from 'react';
import {
  ArrowUpRight,
  LayoutDashboard,
  LogOut,
  Monitor,
  Printer,
  QrCode,
  Smartphone,
} from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLogoutMutation } from '../store/api/authApi';
import { useAppDispatch } from '../store/hooks';
import { setTokensAndUser, REFRESH_TOKEN_KEY } from '../store/slices/authSlice';

const features = [
  {
    icon: LayoutDashboard,
    title: 'Live operations',
    description:
      'Track jobs, revenue, and approvals from one dashboard built for the print counter.',
  },
  {
    icon: QrCode,
    title: 'QR-led ordering',
    description:
      'Customers scan, upload, and pay from their phone instead of handing over files manually.',
  },
  {
    icon: Printer,
    title: 'Connected printing',
    description:
      'The agent links your system to the print queue so paid jobs move faster to execution.',
  },
  {
    icon: Smartphone,
    title: 'Remote oversight',
    description:
      'Keep the desk visible even when you are away from the counter or working across shifts.',
  },
];

const downloadLinks = {
  windows:
    'https://github.com/Dk007ms/Statioprint-Agent/releases/latest/download/StatioPrint-Agent-windows-setup.exe',
  linux:
    'https://github.com/Dk007ms/Statioprint-Agent/releases/latest/download/StatioPrint-Agent-linux.AppImage',
};

export default function SellerPage() {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [logout] = useLogoutMutation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const at = params.get('at');
    const rt = params.get('rt');
    if (at && rt) {
      dispatch(setTokensAndUser({ accessToken: at, refreshToken: rt }));
      window.history.replaceState({}, document.title, '/seller');
    }
  }, [dispatch]);

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/seller/dashboard" replace />;
  }

  const handleGoogleSignIn = () => {
    const backendUrl = import.meta.env.DEV ? '' : (import.meta.env.VITE_BACKEND_URL ?? '');
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  const handleLogout = async () => {
    const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
    await logout({ refreshToken: rt ?? undefined });
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-800">
      <section className="px-4 pt-6 pb-18 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <img
                src="/statiomart.png"
                alt="StatioMart"
                className="h-12 w-12 rounded-2xl object-cover"
              />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                  Shop Owner Workspace
                </p>
                <h1 className="text-2xl font-semibold text-slate-900">StatioPrint Console</h1>
              </div>
            </div>

            {isLoading ? (
              <div className="h-11 w-36 rounded-full bg-white/70 animate-pulse" />
            ) : isAuthenticated && user ? (
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/seller/dashboard"
                  className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white"
                >
                  Open dashboard
                </Link>
                <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2">
                  {user.picture && (
                    <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
                  )}
                  <span className="text-sm text-slate-700">{user.name}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-700 transition-colors hover:border-slate-400"
                  title="Sign out"
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleGoogleSignIn}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:bg-slate-50"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Sign in with Google
                </button>
                <a
                  href="https://statiomart.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:border-slate-500"
                >
                  Visit statiomart.com
                  <ArrowUpRight size={16} />
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-4xl pt-16 pb-10 text-center sm:pt-24">
            <div className="inline-flex items-center rounded-full border border-slate-300 bg-white/80 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
              Powered by StatioMart
            </div>

            <h2 className="mx-auto mt-6 max-w-3xl text-5xl font-semibold tracking-[-0.05em] text-slate-950 sm:text-6xl">
              Operate your print desk with the same clarity as the storefront.
            </h2>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
              StatioPrint gives shop owners a cleaner workflow for QR-led document uploads, faster
              approvals, connected printing, and a stronger StatioMart brand presence across the
              customer journey.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={downloadLinks.windows}
                className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-medium text-white"
              >
                <Monitor size={18} />
                Download for Windows
              </a>
              <a
                href={downloadLinks.linux}
                className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-full border border-slate-300 bg-white/85 px-6 py-3 text-sm font-medium text-slate-800 transition-colors hover:border-slate-500"
              >
                <Monitor size={18} />
                Download for Linux
              </a>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl border-t border-slate-200 pt-8">
          <div className="grid gap-4 text-left md:grid-cols-3">
            <div className="rounded-[1.5rem] bg-white/70 p-5">
              <p className="text-3xl font-semibold tracking-[-0.04em] text-slate-950">1 desk</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                One workflow for walk-in uploads, approvals, payments, and final print execution.
              </p>
            </div>
            <div className="rounded-[1.5rem] bg-white/70 p-5">
              <p className="text-3xl font-semibold tracking-[-0.04em] text-slate-950">Live queue</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Keep the print counter organized with a clearer job stream and less manual
                back-and-forth.
              </p>
            </div>
            <div className="rounded-[1.5rem] bg-white/70 p-5">
              <p className="text-3xl font-semibold tracking-[-0.04em] text-slate-950">StatioMart</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Present printing as part of your broader stationery business, not a disconnected
                tool.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-6 sm:px-6 lg:px-10">
        <div className="mx-auto mt-16 max-w-5xl">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
              What You Get
            </p>
            <h3 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
              Built for everyday counter operations.
            </h3>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="rounded-[1.75rem] border border-slate-200 bg-white/70 p-6 shadow-[0_10px_35px_rgba(15,23,42,0.04)] backdrop-blur-sm"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700">
                    <Icon size={18} />
                  </div>
                  <h4 className="mt-6 text-xl font-semibold text-slate-950">{feature.title}</h4>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 pb-6 sm:px-6 lg:px-10">
        <div className="mx-auto mt-16 grid max-w-5xl gap-8 rounded-[2rem] border border-slate-200 bg-white/70 px-6 py-8 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur-sm lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
              Operating Model
            </p>
            <h3 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
              The print workflow stays aligned with the StatioMart brand.
            </h3>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600">
              Customers experience one connected business: scan in store, upload with StatioPrint,
              and continue discovering the wider StatioMart stationery offering through the same
              identity and support system.
            </p>
          </div>

          <div className="space-y-4">
            <div className="rounded-[1.25rem] bg-white/80 px-4 py-4">
              <p className="text-sm font-medium text-slate-950">Store</p>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Shop No.36, Nathu Singh Market, Masoodpur, Vasant Kunj, New Delhi - 110070
              </p>
            </div>
            <div className="rounded-[1.25rem] bg-white/80 px-4 py-4">
              <p className="text-sm font-medium text-slate-950">Support</p>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                info@statiomart.com • +91 93509 76292
              </p>
            </div>
            <div className="rounded-[1.25rem] bg-white/80 px-4 py-4">
              <p className="text-sm font-medium text-slate-950">Catalog</p>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Cross-sell stationery orders directly through statiomart.com while running print
                operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 pb-20 sm:px-6 lg:px-10">
        <div className="mx-auto mt-12 max-w-5xl text-center">
          <p className="text-sm text-slate-500">
            Looking to print a document?{' '}
            <Link to="/customer" className="font-medium text-slate-800 hover:underline">
              Go to customer page
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
